import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, X, Calculator, Settings2 } from "lucide-react";

// Bare-bones tiered fare calculator (mirror of backend compute_fare) — for the preview widget.
const computeFare = (s, distance = 10, duration = 15, waiting = 0) => {
  const base = Number(s.base_fare) || 0;
  const perKm = Number(s.per_km) || 0;
  const t1 = Number(s.threshold_1_km) || 0;
  const p1 = Number(s.per_km_after_t1) || 0;
  const t2 = Number(s.threshold_2_km) || 0;
  const p2 = Number(s.per_km_after_t2) || 0;
  let distComp;
  if (t1 && p1) {
    const remainder = Math.max(0, distance - t1);
    if (t2 && p2) {
      const d2 = Math.min(remainder, Math.max(0, t2 - t1));
      const d3 = Math.max(0, remainder - d2);
      distComp = d2 * p1 + d3 * p2;
    } else distComp = remainder * p1;
  } else distComp = distance * perKm;
  const time = (Number(s.per_minute) || 0) * duration;
  const wait = (Number(s.waiting_per_minute) || 0) * waiting;
  const pickup = Number(s.pickup_charge) || 0;
  let subtotal = base + distComp + time + wait + pickup;
  const min = Number(s.min_fare) || 0;
  if (min && subtotal < min) subtotal = min;
  const taxPct = Number(s.tax_percent) || 0;
  const tax = subtotal * (taxPct / 100);
  const total = subtotal + tax;
  const commPct = Number(s.commission_percent) || 0;
  const commission = total * (commPct / 100);
  return {
    base, distance: distComp, time, waiting: wait, pickup, subtotal, tax, total,
    commission, driver_earnings: total - commission,
  };
};

const emptyForm = {
  name: "", description: "",
  base_fare: 8.5, per_km: 2.2, passenger_capacity: 4, active: true,
  min_fare: 12.0, threshold_1_km: 0, per_km_after_t1: 0, threshold_2_km: 0, per_km_after_t2: 0,
  per_minute: 0.6, waiting_per_minute: 0.4, pickup_charge: 0, cancellation_fee: 8,
  commission_percent: 20, tax_percent: 15,
  custom_fields: [],
};

const F = ({ label, k, form, setForm, hint, step = "0.01", suffix = "$" }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</Label>
    <div className="relative mt-1">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">{suffix}</span>
      <Input type="number" step={step} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-black/30 border-white/10 h-10 pl-6 text-sm" data-testid={`service-${k}`} />
    </div>
    {hint && <div className="text-[10px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

const ServiceForm = ({ form, setForm, onSubmit, submitLabel = "Save" }) => {
  const preview = computeFare(form, 10, 15, 0);
  return (
    <form onSubmit={onSubmit} className="grid gap-5 max-h-[75vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase text-slate-400">Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" data-testid="service-name" /></div>
        <div><Label className="text-xs uppercase text-slate-400">Capacity</Label><Input type="number" value={form.passenger_capacity} onChange={(e) => setForm({ ...form, passenger_capacity: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" data-testid="service-capacity" /></div>
      </div>
      <div><Label className="text-xs uppercase text-slate-400">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-black/30 border-white/10 mt-1" /></div>

      <div className="border-t border-white/5 pt-4">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Base fare structure</div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Base fare" k="base_fare" form={form} setForm={setForm} hint="Charged for every ride" />
          <F label="Min fare" k="min_fare" form={form} setForm={setForm} hint="Fallback floor" />
          <F label="Pickup charge" k="pickup_charge" form={form} setForm={setForm} hint="Fixed pickup fee" />
        </div>
      </div>

      <div className="border-t border-white/5 pt-4">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Distance tiers</div>
        <div className="grid grid-cols-3 gap-3 items-end">
          <F label="Base per km (fallback)" k="per_km" form={form} setForm={setForm} hint="Used if no tiers set" />
          <F label="Threshold 1 (km)" k="threshold_1_km" form={form} setForm={setForm} step="0.1" suffix="km" hint="Included in base" />
          <F label="Per km after T1" k="per_km_after_t1" form={form} setForm={setForm} hint="Rate T1 → T2" />
          <F label="Threshold 2 (km)" k="threshold_2_km" form={form} setForm={setForm} step="0.1" suffix="km" hint="0 = no T2" />
          <F label="Per km after T2" k="per_km_after_t2" form={form} setForm={setForm} hint="Rate beyond T2" />
        </div>
      </div>

      <div className="border-t border-white/5 pt-4">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Time & wait</div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Per minute" k="per_minute" form={form} setForm={setForm} hint="During the ride" />
          <F label="Waiting per min" k="waiting_per_minute" form={form} setForm={setForm} hint="Idle at pickup" />
          <F label="Cancellation fee" k="cancellation_fee" form={form} setForm={setForm} hint="Charged on no-show" />
        </div>
      </div>

      <div className="border-t border-white/5 pt-4">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Commission & tax</div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Commission %" k="commission_percent" form={form} setForm={setForm} step="0.1" suffix="%" hint="Platform take from driver" />
          <F label="Tax %" k="tax_percent" form={form} setForm={setForm} step="0.1" suffix="%" hint="GST or equivalent" />
        </div>
      </div>

      {/* Fare preview */}
      <div className="border-t border-white/5 pt-4 bg-amber-400/5 -mx-6 px-6 py-4 rounded-lg" data-testid="service-fare-preview">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="w-4 h-4 text-amber-400" />
          <div className="text-sm font-semibold text-amber-300">Preview — 10 km · 15 min · 0 min wait</div>
        </div>
        <div className="grid grid-cols-2 gap-1.5 text-xs font-mono">
          <span className="text-slate-400">Base</span><span className="text-right text-slate-200">NZ$ {preview.base.toFixed(2)}</span>
          <span className="text-slate-400">Distance</span><span className="text-right text-slate-200">NZ$ {preview.distance.toFixed(2)}</span>
          <span className="text-slate-400">Time</span><span className="text-right text-slate-200">NZ$ {preview.time.toFixed(2)}</span>
          <span className="text-slate-400">Pickup</span><span className="text-right text-slate-200">NZ$ {preview.pickup.toFixed(2)}</span>
          <span className="text-slate-400">Subtotal</span><span className="text-right text-slate-100">NZ$ {preview.subtotal.toFixed(2)}</span>
          <span className="text-slate-400">Tax</span><span className="text-right text-slate-200">NZ$ {preview.tax.toFixed(2)}</span>
          <span className="border-t border-white/10 pt-1 text-amber-300 font-semibold">Rider pays</span>
          <span className="border-t border-white/10 pt-1 text-right text-amber-300 font-bold text-sm">NZ$ {preview.total.toFixed(2)}</span>
          <span className="text-slate-400">Commission</span><span className="text-right text-slate-200">- NZ$ {preview.commission.toFixed(2)}</span>
          <span className="text-emerald-400 font-semibold">Driver keeps</span><span className="text-right text-emerald-400 font-semibold">NZ$ {preview.driver_earnings.toFixed(2)}</span>
        </div>
      </div>

      <div className="border-t border-white/5 pt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase text-slate-400">Custom questions</Label>
          <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, custom_fields: [...(form.custom_fields || []), { key: "", label: "", type: "text", required: false }] })} className="bg-transparent border-amber-400/40 text-amber-400 h-7" data-testid="service-add-custom-field"><Plus className="w-3 h-3 mr-1" /> Add</Button>
        </div>
        {(form.custom_fields || []).map((cf, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
            <Input value={cf.key} onChange={(e) => { const nf = [...form.custom_fields]; nf[i] = { ...nf[i], key: e.target.value.replace(/\s+/g,"_").toLowerCase() }; setForm({ ...form, custom_fields: nf }); }} placeholder="key" className="col-span-3 bg-black/30 border-white/10 h-9 text-xs font-mono" />
            <Input value={cf.label} onChange={(e) => { const nf = [...form.custom_fields]; nf[i] = { ...nf[i], label: e.target.value }; setForm({ ...form, custom_fields: nf }); }} placeholder="Question label" className="col-span-5 bg-black/30 border-white/10 h-9 text-xs" />
            <select value={cf.type} onChange={(e) => { const nf = [...form.custom_fields]; nf[i] = { ...nf[i], type: e.target.value }; setForm({ ...form, custom_fields: nf }); }} className="col-span-2 bg-black/30 border border-white/10 rounded-md h-9 text-xs px-2">
              <option value="text">Text</option><option value="number">Number</option><option value="checkbox">Yes/No</option>
            </select>
            <label className="col-span-1 text-[10px] text-slate-400 flex items-center gap-1"><input type="checkbox" checked={cf.required} onChange={(e) => { const nf = [...form.custom_fields]; nf[i] = { ...nf[i], required: e.target.checked }; setForm({ ...form, custom_fields: nf }); }} /> Req</label>
            <button type="button" onClick={() => setForm({ ...form, custom_fields: form.custom_fields.filter((_,j)=>j!==i) })} className="col-span-1 text-red-400 hover:bg-red-500/10 rounded p-1"><X className="w-4 h-4 mx-auto" /></button>
          </div>
        ))}
      </div>

      <Button type="submit" className="btn-gold" data-testid="service-submit">{submitLabel}</Button>
    </form>
  );
};

export default function ServicesTab() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try { const { data } = await api.get("/services"); setServices(data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const toNumbers = (f) => {
    const out = { ...f };
    ["base_fare","per_km","passenger_capacity","min_fare","threshold_1_km","per_km_after_t1",
     "threshold_2_km","per_km_after_t2","per_minute","waiting_per_minute","pickup_charge",
     "cancellation_fee","commission_percent","tax_percent"].forEach((k) => {
      out[k] = out[k] === "" || out[k] == null ? 0 : Number(out[k]);
    });
    out.custom_fields = (out.custom_fields || []).filter(f => f.key && f.label);
    return out;
  };

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/services", toNumbers(form)); toast.success("Service added"); setOpen(false); setForm(emptyForm); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openEdit = (s) => { setEditing(s); setForm({ ...emptyForm, ...s }); };
  const save = async (e) => {
    e.preventDefault();
    try { await api.patch(`/services/${editing.id}`, toNumbers(form)); toast.success("Service saved"); setEditing(null); setForm(emptyForm); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async (s) => {
    try { await api.patch(`/services/${s.id}`, { active: !s.active }); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/services/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <Header title="Services & Pricing" subtitle="Vehicle types with tiered fares, commission and taxes">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyForm); }}>
          <DialogTrigger asChild><Button className="btn-gold rounded-full" data-testid="services-add"><Plus className="w-4 h-4 mr-2" /> New service</Button></DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 text-white max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">New vehicle type</DialogTitle></DialogHeader>
            <ServiceForm form={form} setForm={setForm} onSubmit={add} submitLabel="Create service" />
          </DialogContent>
        </Dialog>
      </Header>

      <div className="grid md:grid-cols-2 gap-4">
        {services.map((s) => {
          const preview = computeFare(s, 10, 15, 0);
          return (
            <div key={s.id} className="glass-card rounded-2xl p-6 gold-border" data-testid={`service-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-xl font-bold">{s.name}</div>
                  <p className="text-sm text-slate-400 mt-1">{s.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} />
                  <Button variant="outline" size="icon" onClick={() => openEdit(s)} className="bg-transparent border-white/10 text-slate-300 h-8 w-8" data-testid={`service-edit-${s.id}`}><Settings2 className="w-3.5 h-3.5" /></Button>
                  <Button variant="outline" size="icon" onClick={() => del(s.id)} className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-4 gap-2 text-center">
                <div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Base</div><div className="font-mono text-amber-400 mt-1 text-sm">NZ$ {Number(s.base_fare || 0).toFixed(2)}</div></div>
                <div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Min</div><div className="font-mono text-slate-300 mt-1 text-sm">NZ$ {Number(s.min_fare || 0).toFixed(2)}</div></div>
                <div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Comm</div><div className="font-mono text-slate-300 mt-1 text-sm">{Number(s.commission_percent || 0).toFixed(0)}%</div></div>
                <div><div className="text-[10px] text-slate-500 uppercase tracking-wider">Pax</div><div className="font-mono text-white mt-1 text-sm">{s.passenger_capacity}</div></div>
              </div>
              <div className="mt-3 bg-black/20 rounded-lg p-3 text-xs">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">10 km / 15 min preview</div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Rider pays</span>
                  <span className="text-amber-400 font-semibold font-mono">NZ$ {preview.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-emerald-400/80">Driver keeps</span>
                  <span className="text-emerald-400 font-mono">NZ$ {preview.driver_earnings.toFixed(2)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Edit {editing?.name}</DialogTitle></DialogHeader>
          <ServiceForm form={form} setForm={setForm} onSubmit={save} submitLabel="Save changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
